import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UploadFile } from 'antd/es/upload/interface';

export const FILE_SIZE_LIMIT = 10 * 1024 * 1024; // 10MB
export const SAMPLE_CSV_LINK = '/public-assets/sample_lead_activity_import.csv';

export enum ImportActivitySteps {
  UploadFile = 0,
  MapFields = 1,
  Review = 2,
  Success = 3,
}

export interface FieldError {
  showFieldErrors: boolean;
  link: string;
  preview: Array<string[]>;
  total_errors: number;
}

export interface SearchKeyConfig {
  searchKey: string;
  searchKeyHeader: string;
}

export interface FieldMapping {
  schemaName: string;
  format?: string;
  timezone?: string;
}

export interface MappingConfig {
  activityId: string | number;
  searchKeyConfig: SearchKeyConfig;
  mapping: Record<string, FieldMapping>;
}

export interface UploadedFileConfig {
  file?: UploadFile | null;
  progessPercent?: number;
  progressStatus?: 'active' | 'success' | 'exception';
}

export interface ImportActivityState {
  isModalOpen: boolean;
  currentStep: ImportActivitySteps;
  uploadedFile: UploadedFileConfig;
  requestUuid: string;
  csvHeaders: string[];
  mappingConfig: MappingConfig;
  fieldErrors: FieldError;
  lastPayload: any;
}

const defaultFieldErrors: FieldError = {
  showFieldErrors: false,
  link: '',
  preview: [],
  total_errors: 0,
};

const initialState: ImportActivityState = {
  isModalOpen: false,
  currentStep: ImportActivitySteps.UploadFile,
  uploadedFile: {
    file: null,
    progessPercent: 0,
    progressStatus: 'active',
  },
  requestUuid: '',
  csvHeaders: [],
  mappingConfig: {
    activityId: '',
    searchKeyConfig: {
      searchKey: '',
      searchKeyHeader: '',
    },
    mapping: {},
  },
  fieldErrors: defaultFieldErrors,
  lastPayload: {},
};

const importActivitySlice = createSlice({
  name: 'importActivitySlice',
  initialState,
  reducers: {
    openImportActivityModal: (state) => {
      state.isModalOpen = true;
    },
    updateUploadedFileConfig: (state, action: PayloadAction<UploadedFileConfig>) => {
      state.uploadedFile = {
        ...state.uploadedFile,
        ...action.payload,
      };
    },
    updateStep: (state, action: PayloadAction<ImportActivitySteps>) => {
      state.currentStep = action.payload;
    },
    updateRequestData: (
      state,
      action: PayloadAction<Partial<ImportActivityState>>,
    ) => ({ ...state, ...action.payload }),
    updateMappingConfig: (state, action: PayloadAction<{
      field: keyof MappingConfig;
      value: any;
    }>) => {
      state.mappingConfig[action.payload.field] = action.payload.value;
    },
    updateFieldMapping: (state, action: PayloadAction<{
      fieldName: string;
      mapping: FieldMapping;
    }>) => {
      state.mappingConfig.mapping[action.payload.fieldName] = action.payload.mapping;
    },
    deleteFieldMapping: (state, action: PayloadAction<string>) => {
      const { [action.payload]: removed, ...rest } = state.mappingConfig.mapping;
      state.mappingConfig.mapping = rest;
    },
    updateFieldErrors: (state, action: PayloadAction<Partial<FieldError>>) => {
      state.fieldErrors = {
        ...state.fieldErrors,
        ...action.payload,
      };
    },
    resetImportActivity: () => initialState,
  },
});

export const {
  openImportActivityModal,
  updateUploadedFileConfig,
  updateStep,
  updateRequestData,
  updateMappingConfig,
  updateFieldMapping,
  deleteFieldMapping,
  updateFieldErrors,
  resetImportActivity,
} = importActivitySlice.actions;

export default importActivitySlice.reducer;
